import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Rate } from '../../../shared/types/Rate';
import { MoreHorizontalIcon, PlusIcon, GripVerticalIcon } from 'lucide-react';
export function Rates() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [rates, setRates] = useState<Rate[]>([{
    id: '1',
    name: 'Test With Scuba Spa',
    description: 'Scuba spa test',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES',
    baseNightlyRate: 100.0,
    cancellationPlan: 'NON_REFUNDABLE',
    status: 'inactive',
    isPromo: true
  }, {
    id: '2',
    name: 'Advance Booking with Free Seaplan Transfer',
    description: 'Plan your Langkawi escape early and enjoy the added luxury of a complimentary round-trip ferry transfer from Kuala Kedah to the resort. This exclusive offer rewards advance planners with significant savings on transportation costs.',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES2025',
    baseNightlyRate: 100.0,
    cancellationPlan: 'FLEXIBLE',
    status: 'active',
    isPromo: true
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('all');
  // Filter rates based on search term and filters
  const filteredRates = rates.filter(rate => {
    const matchesSearch = rate.name.toLowerCase().includes(searchTerm.toLowerCase()) || rate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && rate.status === 'active' || statusFilter === 'inactive' && rate.status === 'inactive';
    const matchesRoomType = roomTypeFilter === 'all' || rate.roomType === roomTypeFilter;
    return matchesSearch && matchesStatus && matchesRoomType;
  });
  // Format currency for display
  function formatCurrency(amount: number): string {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  }
  // Format cancellation plan for display
  function formatCancellationPlan(plan: string): string {
    return plan.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }
  return <div className="space-y-6" data-id="element-10389">
      <div className="flex justify-between items-center" data-id="element-10390">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-10391">
          {t('pages.Rates.rates')}
        </h1>
        <button className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-[#1A1A1A] px-4 py-2 rounded-md flex items-center" data-id="element-10392">
          <PlusIcon size={18} className="mr-1" data-id="element-10393" />
          {t('pages.Rates.add_rates')}
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-10394">
        <input type="search" aria-label={t('pages.Rates.attrs.search_rates')} placeholder={t('pages.Rates.attrs.search_rates')} className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10395" />

        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-10396">
          <option value="all" data-id="element-10397">{t('components.AddRoomForm.status')}</option>
          <option value="active" data-id="element-10398">{t('pages.Rates.active')}</option>
          <option value="inactive" data-id="element-10399">{t('pages.Rates.inactive')}</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} data-id="element-10400">
          <option value="all" data-id="element-10401">{t('pages.Rates.room_type')}</option>
          <option value="DELUXE" data-id="element-10402">{t('pages.Rates.deluxe')}</option>
          <option value="SUPERIOR" data-id="element-10403">{t('pages.Rates.superior')}</option>
          <option value="OVERWATER_VILLA" data-id="element-10404">
            {t('pages.Rates.overwater_villa')}
          </option>
        </select>
      </div>
      {/* Rates List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-10405">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10406">
          <thead className="bg-gray-50" data-id="element-10407">
            <tr data-id="element-10408">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10409">
                {t('pages.Rates.details')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10410">
                {t('pages.Rates.promo_code')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10411">
                {t('pages.Rates.base_nightly_rate')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10412">
                {t('pages.Rates.cancellation_plan')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10413">
                {t('components.AddRoomForm.status')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10414">
                {t('components.RoomAssets.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10415">
            {filteredRates.map(rate => <tr key={rate.id} data-id="element-10416">
                <td className="px-6 py-4" data-id="element-10417">
                  <div className="flex items-start" data-id="element-10418">
                    <GripVerticalIcon size={16} className="text-gray-400 mt-1 mr-2" data-id="element-10419" />

                    <div data-id="element-10420">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10421">
                        {rate.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 max-w-lg" data-id="element-10422">
                        {rate.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10423">
                  <div className="text-sm text-gray-900" data-id="element-10424">{rate.promoCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10425">
                  <div className="text-sm text-gray-900" data-id="element-10426">
                    {formatCurrency(rate.baseNightlyRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10427">
                  <div className="text-sm text-gray-900" data-id="element-10428">
                    {formatCancellationPlan(rate.cancellationPlan)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10429">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10430">
                    {rate.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {rate.isPromo && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-10431">
                      {t('pages.Rates.promo')}
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-10432">
                  <button aria-label="Rate actions" className="text-gray-400 hover:text-gray-500" data-id="element-10433">
                    <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-10434" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}